import { useEffect } from "react";

import isEmpty from "lodash/isEmpty";

import { filterToAllowedUrlParams } from "@/constants/WebSessionRouteParams";
import { router } from "@/crossPlatform/expo-router";
import { readAndClearReturnToUrl } from "@/utils/browserSessionStorage.util";
import { cleanDeepLinkPath } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";

// on web, only AFTER reaching the home tab:
// restore the user to the URL they visited before they were redirected to Auth0's login page
const useRestoreReturnToUrl = (): void => {
  useEffect(() => {
    if (!isWeb()) return;
    const returnTo = readAndClearReturnToUrl();
    if (!isNotNilNotEmpty(returnTo)) return;

    // pass pathname and query params separately so that web expo-router correctly propagates them
    const cleaned = cleanDeepLinkPath(returnTo);
    const [pathname, search] = cleaned.split("?");
    // filter user-provided URL params to only allowlisted ones, to prevent user changing arbitrary nav params
    const params = filterToAllowedUrlParams(search);

    // expo-router only routes within the app, no concerns of handling eg: returnTo=https://some-other-website.com
    const href = isEmpty(params) ? pathname : { params, pathname };

    // on web, when a user navigates directly to a URL, this useEffect runs after home tab's mount
    // however, that doesn't mean that home tab has been painted to the screen.
    // so a direct URL like /video-content will route and render that modal, with nothing underneath (gray background)
    // so, due to lack of any proper alternative, a double animation frame wait is forced to approximate "probably painted by now"
    let innerRafId = 0;
    const outerRafId = requestAnimationFrame(() => {
      // navigate (not push): for a /home?target=... returnTo, we're already on the home tab, and navigate reuses
      // the current route (firing a re-render so home's useSubDeepLink picks up target) rather than pushing a duplicate home;
      // for off-home returnTos (eg /video-content) navigate still pushes since we aren't on that route
      innerRafId = requestAnimationFrame(() => router.navigate(href));
    });
    return () => {
      cancelAnimationFrame(outerRafId);
      cancelAnimationFrame(innerRafId);
    };
  }, []);
};

export default useRestoreReturnToUrl;
